import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Plus, Trash2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { Student } from "@/types";
import { useQueryClient } from "@tanstack/react-query";

interface MonitoringParameter {
  id: string;
  name: string;
  unit: string;
  minValue: string; // store as string to allow free typing
  maxValue: string; // store as string to allow free typing
}

interface MonitoringProfileDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  student: Student;
  termId: string;
  existingProfile?: any;
}

export function MonitoringProfileDialog({ 
  open, 
  onOpenChange, 
  student,
  termId,
  existingProfile 
}: MonitoringProfileDialogProps) {
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [parameters, setParameters] = useState<MonitoringParameter[]>([]);
  const [notes, setNotes] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Load existing profile data when editing
  useState(() => {
    if (existingProfile && open) {
      const existingParams = (existingProfile.parameters as any[]) || [];
      setParameters(existingParams.map((p: any) => ({
        id: crypto.randomUUID(),
        name: p.name,
        unit: p.unit,
        minValue: String(p.min),
        maxValue: String(p.max),
      })));
      setNotes(existingProfile.notes || "");
    } else if (!existingProfile && open) {
      setParameters([]);
      setNotes("");
    }
  });

  const addParameter = () => {
    setParameters([
      ...parameters,
      {
        id: crypto.randomUUID(),
        name: "",
        unit: "",
        minValue: "",
        maxValue: "",
      },
    ]);
  };

  const removeParameter = (id: string) => {
    setParameters(parameters.filter((p) => p.id !== id));
  };

  const updateParameter = (id: string, field: keyof MonitoringParameter, value: any) => {
    setParameters(
      parameters.map((p) => (p.id === id ? { ...p, [field]: value } : p))
    );
  };

  const handleNumberChange = (id: string, field: 'minValue' | 'maxValue', value: string) => {
    updateParameter(id, field, value);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user?.schoolId || !user?.profileId || parameters.length === 0) {
      toast({
        title: "Error",
        description: "Missing required information. Please ensure you're logged in properly.",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);
    try {
      const profileData = {
        student_id: student.id,
        term_id: termId,
        nurse_id: user.profileId,
        school_id: user.schoolId,
        parameters: parameters.map(p => ({
          name: p.name.trim(),
          unit: p.unit.trim(),
          min: parseFloat(p.minValue),
          max: parseFloat(p.maxValue),
        })),
        notes,
        is_active: true,
      };

      let error;
      if (existingProfile) {
        // Update existing profile
        const { error: updateError } = await supabase
          .from('special_monitoring_profiles')
          .update(profileData)
          .eq('id', existingProfile.id);
        error = updateError;
      } else {
        // Create new profile
        const { error: insertError } = await supabase
          .from('special_monitoring_profiles')
          .insert(profileData);
        error = insertError;
      }

      if (error) {
        console.error('Database error:', error);
        throw error;
      }

      toast({
        title: "Success",
        description: existingProfile 
          ? "Monitoring profile updated successfully" 
          : "Monitoring profile created successfully",
      });

      queryClient.invalidateQueries({ queryKey: ['monitoringProfile', student.id, termId] });
      queryClient.invalidateQueries({ queryKey: ['activeMonitoringProfiles'] });
      onOpenChange(false);
      setParameters([]);
      setNotes("");
    } catch (error: any) {
      console.error('Error saving profile:', error);
      const errorMessage = error?.message || error?.error_description || "Failed to save profile";
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {existingProfile ? 'Edit' : 'Create'} Monitoring Profile for {student.surname}, {student.other_names}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <Label>Monitoring Parameters</Label>
              <Button type="button" size="sm" onClick={addParameter}>
                <Plus className="h-4 w-4 mr-2" />
                Add Parameter
              </Button>
            </div>

            {parameters.length === 0 && (
              <div className="text-center py-8 text-muted-foreground border-2 border-dashed rounded-lg">
                No parameters added. Click "Add Parameter" to get started.
              </div>
            )}

            {parameters.map((param) => (
              <div key={param.id} className="p-4 border rounded-lg space-y-3">
                <div className="flex justify-between items-start">
                  <div className="grid grid-cols-2 gap-3 flex-1">
                    <div>
                      <Label className="text-xs">Parameter Name</Label>
                      <Input
                        placeholder="e.g., Blood Pressure"
                        value={param.name}
                        onChange={(e) =>
                          updateParameter(param.id, "name", e.target.value)
                        }
                        required
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Unit</Label>
                      <Input
                        placeholder="e.g., mmHg"
                        value={param.unit}
                        onChange={(e) =>
                          updateParameter(param.id, "unit", e.target.value)
                        }
                        required
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Min Value</Label>
                      <Input
                        type="text"
                        inputMode="decimal"
                        pattern="[0-9]*[.,]?[0-9]*"
                        value={param.minValue}
                        onChange={(e) =>
                          handleNumberChange(param.id, "minValue", e.target.value)
                        }
                        required
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Max Value</Label>
                      <Input
                        type="text"
                        inputMode="decimal"
                        pattern="[0-9]*[.,]?[0-9]*"
                        value={param.maxValue}
                        onChange={(e) =>
                          handleNumberChange(param.id, "maxValue", e.target.value)
                        }
                        required
                      />
                    </div>
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => removeParameter(param.id)}
                    className="ml-2"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>

          <div>
            <Label>Notes (Optional)</Label>
            <Textarea
              placeholder="Add any additional notes about this monitoring profile..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
            />
          </div>

          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting || parameters.length === 0}>
              {existingProfile ? 'Update Profile' : 'Create Profile'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
